import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSchool } from "../../../Hooks/SchoolContext";
import { regionOf } from "../../../utils/region";
import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SessionDetail from "./SessionDetail";
import { openSessionRoom } from "../../../utils/sessionRoom";
import { secondaryButtonSx } from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * /school/sessions/:id — one session, at its own address.
 *
 * WHY A URL AND NOT ONLY A PANEL
 * A specific session was only ever reachable by finding the row that produced
 * it. You could not send a colleague "look at this one", could not bookmark
 * the lesson you are about to teach, could not open two in tabs to compare,
 * and the browser's back button did not know a panel had opened — so back
 * left the whole page instead of closing it.
 *
 * That matters most for the thing this product is actually selling. A
 * recording, a recap and a debrief are the record of something a student paid
 * for; a record with no address is a record you can only reach by remembering
 * where it was.
 *
 * THE PANEL IS NOT REPLACED. Opening a session from inside a list without
 * losing your place in the list is a real need, and a full navigation for a
 * glance is a worse trade. Both frames render the same component — nothing
 * inside it knows which it is in, so they cannot drift.
 *
 * BACK, NOT CLOSE. This page is usually arrived at from the diary or the
 * ledger, and history.back() returns to that scroll position; a hardcoded
 * link to one of them would send half the arrivals somewhere they did not
 * come from.
 */
export default function SessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { school } = useSchool();

  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/school/teaching");
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: "auto" }}>
      <Button
        onClick={back}
        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
        sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6, px: 1.4, fontSize: "12.5px", mb: 1.5 }}
      >
        Back
      </Button>
      <SessionDetail
        id={id}
        asPage
        onClose={back}
        region={regionOf(school)}
        onJoin={(s) => openSessionRoom(s.id)}
      />
    </Box>
  );
}
